import React, { useState, useEffect } from 'react';
import { Table, Pagination, Spinner, Alert, Container, Form, Button, Card, Row, Col, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BsEye, BsPencil, BsPlusCircle } from 'react-icons/bs';
import { toast } from 'react-toastify';
import api from '../../api';
import EditarCandidatoModal from './modal/EditarCandidatoModal';
import CriarCandidatoModal from './modal/CriarCandidatoModal';
import SolicitarAlteracaoSensivelCandidatoModal from './modal/SolicitarAlteracaoSensivelCandidatoModal';
import PermissaoWrapper from '../../components/PermissaoWrapper';
import { PermissaoEnum } from '../../model/Usuario/Permissao';

interface CandidatoEdital {
    id: string;
    inscricao: string;
    candidatoId: string;
    editalId: string;
    nomeEdital: string;
}

interface CandidatoResponse {
    id: string;
    candidatoEditais: CandidatoEdital[];
    nome: string;
    nomeSocial: string | null;
    cpf: string;
    passaporte: string | null;
    email: string;
    status: string;
    autodeclaracaoRacial?: string | null;
    candidatoResponsavel: any;
    duplicidade: boolean;
}

interface Estatisticas {
    total: number;
    validados: number;
    naoValidados: number;
    ausentes: number;
    inconclusivos: number;
    indefinidos: number;
}

interface CandidatosComEstatisticasResponse {
    candidatos: {
        content: CandidatoResponse[];
        totalElements: number;
        totalPages: number;
        first: boolean;
        last: boolean;
        numberOfElements: number;
        pageable: any;
        size: number;
        number: number;
        sort: any;
        empty: boolean;
    };
    estatisticas: Estatisticas;
}

interface CandidatosUploadListProps {
    processoId: string;
}

const CandidatosUploadList: React.FC<CandidatosUploadListProps> = ({ processoId }) => {
    const [candidatos, setCandidatos] = useState<CandidatoResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [estatisticas, setEstatisticas] = useState<Estatisticas | null>(null);
    
    // Filtros
    const [filtroNome, setFiltroNome] = useState('');
    const [filtroInscricao, setFiltroInscricao] = useState('');
    const [filtroCpf, setFiltroCpf] = useState('');
    const [filtroEmail, setFiltroEmail] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('');
    
    // Modal de edição/visualização
    const [showModal, setShowModal] = useState(false);
    const [candidatoSelecionado, setCandidatoSelecionado] = useState<CandidatoResponse | null>(null);
    const [modoEdicao, setModoEdicao] = useState(false);
    const [showSolicitacaoModal, setShowSolicitacaoModal] = useState(false);
    const [candidatoSolicitacao, setCandidatoSolicitacao] = useState<CandidatoResponse | null>(null);
    
    // Modal de criação
    const [showCriarModal, setShowCriarModal] = useState(false);
    const [processo, setProcesso] = useState<any>(null);

    const fetchCandidatos = async () => {
        try {
            setLoading(true);
            const params: any = { page, size };
            if (filtroNome) params.nome = filtroNome;
            if (filtroInscricao) params.inscricao = filtroInscricao;
            if (filtroCpf) params.cpf = filtroCpf;
            if (filtroEmail) params.email = filtroEmail;
            if (filtroStatus) params.status = filtroStatus;
            
            const response = await api.get<CandidatosComEstatisticasResponse>(`/processos/${processoId}/candidatos/estatisticas`, { params });
            
            setCandidatos(response.data.candidatos.content);
            setTotalPages(response.data.candidatos.totalPages);
            setTotalElements(response.data.candidatos.totalElements);
            setEstatisticas(response.data.estatisticas);
            setError(null);
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || 'Erro ao carregar candidatos';
            setError(errorMsg);
            setCandidatos([]);
            setEstatisticas(null);
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const fetchProcesso = async () => {
        try {
            const response = await api.get(`/processos/${processoId}`);
            setProcesso(response.data);
        } catch (err: any) {
            console.error('Erro ao carregar processo:', err);
        }
    };

    useEffect(() => {
        if (processoId) {
            fetchCandidatos();
            fetchProcesso();
        }
    }, [processoId, page, size, filtroNome, filtroInscricao, filtroCpf, filtroEmail, filtroStatus]);

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSize(Number(e.target.value));
        setPage(0);
    };

    const handleFiltroChange = (tipo: string, valor: string) => {
        setPage(0);
        switch (tipo) {
            case 'nome': setFiltroNome(valor); break;
            case 'inscricao': setFiltroInscricao(valor); break;
            case 'cpf': setFiltroCpf(valor); break;
            case 'email': setFiltroEmail(valor); break;
            case 'status': setFiltroStatus(valor); break;
        }
    };

    const limparFiltros = () => {
        setFiltroNome('');
        setFiltroInscricao('');
        setFiltroCpf('');
        setFiltroEmail('');
        setFiltroStatus('');
        setPage(0);
    };

    const handleVisualizarCandidato = (candidato: CandidatoResponse) => {
        setCandidatoSelecionado(candidato);
        setModoEdicao(false);
        setShowModal(true);
    };

    const handleEditarCandidato = (candidato: CandidatoResponse) => {
        setCandidatoSelecionado(candidato);
        setModoEdicao(true);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCandidatoSelecionado(null);
        setModoEdicao(false);
    };

    const handleAbrirSolicitacaoSensivel = (candidato: CandidatoResponse) => {
        setCandidatoSolicitacao(candidato);
        setShowSolicitacaoModal(true);
    };

    const handleFecharSolicitacaoSensivel = () => {
        setShowSolicitacaoModal(false);
        setCandidatoSolicitacao(null);
    };

    const handleSaveSuccess = () => {
        fetchCandidatos();
    };

    const handleCandidatoCriado = () => {
        fetchCandidatos();
        setShowCriarModal(false);
    };

    // Componente para exibir múltiplas inscrições
    const renderInscricoes = (candidatoEditais: CandidatoEdital[]) => {
        if (candidatoEditais.length === 0) {
            return '-';
        } else if (candidatoEditais.length === 1) {
            return (
                <div>
                    {candidatoEditais[0].inscricao} - {candidatoEditais[0].nomeEdital}
                </div>
            );
        } else {
            return (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip id={`tooltip-${candidatoEditais[0].candidatoId}`}>
                            {candidatoEditais.map((edital, index) => (
                                <div key={edital.id}>
                                    {edital.inscricao} - {edital.nomeEdital}
                                    {index < candidatoEditais.length - 1 && <br />}
                                </div>
                            ))}
                        </Tooltip>
                    }
                >
                    <Badge bg="secondary" className="cursor-pointer">
                        {candidatoEditais.length} inscrições
                    </Badge>
                </OverlayTrigger>
            );
        }
    };

    return (
        <Container fluid className="py-4">
            {/* Header com Estatísticas */}
            <Card className="mb-4 shadow-sm">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col>
                            <h4 className="mb-0">Lista de Candidatos</h4>
                            <small className="text-muted">Visualize e gerencie os candidatos do processo</small>
                        </Col>
                        <Col xs="auto" className="d-flex gap-2 align-items-center">
                            <Badge bg="info" className="fs-6">
                                {totalElements} total
                            </Badge>
                            <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.EDITAR_CANDIDATO]}>
                                <Button 
                                    variant="success" 
                                    size="sm"
                                    onClick={() => setShowCriarModal(true)}
                                >
                                    <BsPlusCircle className="me-1" />
                                    Novo Candidato
                                </Button>
                            </PermissaoWrapper>
                        </Col>
                    </Row>
                    {estatisticas && (
                        <Row className="mt-3">
                            <Col>
                                <div className="d-flex gap-3 flex-wrap">
                                    <small className="text-success">
                                        <strong>{estatisticas.validados}</strong> validados
                                    </small>
                                    <small className="text-danger">
                                        <strong>{estatisticas.naoValidados}</strong> não validados
                                    </small>
                                    <small className="text-secondary">
                                        <strong>{estatisticas.ausentes}</strong> ausentes
                                    </small>
                                    <small className="text-warning">
                                        <strong>{estatisticas.inconclusivos}</strong> inconclusivos
                                    </small>
                                    <small className="text-info">
                                        <strong>{estatisticas.indefinidos}</strong> indefinidos
                                    </small>
                                </div>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            {/* Filtros */}
            <Card className="mb-4 shadow-sm">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Nome</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Filtrar por nome..."
                                    value={filtroNome}
                                    onChange={(e) => handleFiltroChange('nome', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Inscrição</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Inscrição..."
                                    value={filtroInscricao}
                                    onChange={(e) => handleFiltroChange('inscricao', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>CPF</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="CPF..."
                                    value={filtroCpf}
                                    onChange={(e) => handleFiltroChange('cpf', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Email..."
                                    value={filtroEmail}
                                    onChange={(e) => handleFiltroChange('email', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Status</Form.Label>
                                <Form.Select 
                                    value={filtroStatus} 
                                    onChange={(e) => handleFiltroChange('status', e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    <option value="VALIDADO">Validado</option>
                                    <option value="VALIDADO_ANTERIORMENTE">Validado anteriormente</option>
                                    <option value="NAO_VALIDADO">Não Validado</option>
                                    <option value="AUSENTE">Ausente</option>
                                    <option value="INCONCLUSIVO">Inconclusivo</option>
                                    <option value="NAO_AVALIADO">Não avaliado</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={1} className="d-flex align-items-end">
                            <Button variant="outline-secondary" onClick={limparFiltros} className="w-100">
                                Limpar
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {error && <Alert variant="danger" className="shadow-sm">{error}</Alert>}

            {loading ? (
                <Card className="shadow-sm">
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" role="status" variant="primary">
                            <span className="visually-hidden">Carregando...</span>
                        </Spinner>
                        <p className="mt-3 text-muted">Carregando candidatos...</p>
                    </Card.Body>
                </Card>
            ) : (
                <>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Nome Social</th>
                                <th>Inscrições</th>
                                <th>CPF</th>
                                <th>Email</th>
                                <th>Status</th>
                                <th style={{ width: '120px' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {candidatos.length > 0 ? (
                                candidatos.map((candidato) => (
                                    <tr key={candidato.id}>
                                        <td>{candidato.nome}</td>
                                        <td>{candidato.nomeSocial || '-'}</td>
                                        <td>{renderInscricoes(candidato.candidatoEditais)}</td>
                                        <td>{candidato.cpf}</td>
                                        <td>{candidato.email}</td>
                                        <td>
                                            <Badge bg={
                                                candidato.status === 'VALIDADO' ? 'success' :
                                                candidato.status === 'VALIDADO_ANTERIORMENTE' ? 'primary' :
                                                candidato.status === 'NAO_VALIDADO' ? 'danger' :
                                                candidato.status === 'AUSENTE' ? 'secondary' :
                                                candidato.status === 'INCONCLUSIVO' ? 'warning' : 'info'
                                            }>
                                                {candidato.status}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.CONSULTAR_CANDIDATO]}>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => handleVisualizarCandidato(candidato)}
                                                        title="Visualizar candidato"
                                                    >
                                                        <BsEye />
                                                    </Button>
                                                </PermissaoWrapper>
                                                <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.EDITAR_CANDIDATO]}>
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        onClick={() => handleEditarCandidato(candidato)}
                                                        title="Editar candidato"
                                                    >
                                                        <BsPencil />
                                                    </Button>
                                                </PermissaoWrapper>
                                                <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.EDITAR_CANDIDATO]}>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleAbrirSolicitacaoSensivel(candidato)}
                                                        title="Solicitar edição de dados sensíveis"
                                                    >
                                                        <i className="bi-shield-lock"></i>
                                                    </Button>
                                                </PermissaoWrapper>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="text-center">
                                        Nenhum candidato encontrado
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="d-flex align-items-center gap-3">
                            <Form.Group className="mb-0">
                                <Form.Label className="me-2 mb-0">Itens por página:</Form.Label>
                                <Form.Select value={size} onChange={handleSizeChange} style={{width: 'auto', display: 'inline-block'}}>
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </Form.Select>
                            </Form.Group>
                            <small className="text-muted">
                                Mostrando {candidatos.length} de {totalElements} candidatos
                            </small>
                        </div>
                        
                        {totalPages > 1 && (
                            <Pagination className="mb-0">
                                <Pagination.First onClick={() => handlePageChange(0)} disabled={page === 0} />
                                <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 0} />
                                
                                {[...Array(totalPages).keys()].map((pageNumber) => (
                                    <Pagination.Item
                                        key={pageNumber}
                                        active={pageNumber === page}
                                        onClick={() => handlePageChange(pageNumber)}
                                    >
                                        {pageNumber + 1}
                                    </Pagination.Item>
                                ))}
                                
                                <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalPages - 1} />
                                <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={page === totalPages - 1} />
                            </Pagination>
                        )}
                    </div>
                </>
            )}

            {/* Modal de Edição/Visualização */}
            {candidatoSelecionado && (
                <EditarCandidatoModal
                    show={showModal}
                    onHide={handleCloseModal}
                    candidato={candidatoSelecionado}
                    editMode={modoEdicao}
                    processoId={processoId}
                    onSaveSuccess={handleSaveSuccess}
                />
            )}

            {/* Modal de Criação */}
            {processo && (
                <CriarCandidatoModal
                    show={showCriarModal}
                    onHide={() => setShowCriarModal(false)}
                    processoId={processoId}
                    onCandidatoCriado={handleCandidatoCriado}
                    processo={processo}
                />
            )}

            {candidatoSolicitacao && (
                <SolicitarAlteracaoSensivelCandidatoModal
                    show={showSolicitacaoModal}
                    onHide={handleFecharSolicitacaoSensivel}
                    candidato={candidatoSolicitacao}
                    processoId={processoId}
                    onSaveSuccess={handleSaveSuccess}
                />
            )}
        </Container>
    );
};

export default CandidatosUploadList;
